import { useDynamicConfig } from '@statsig/react-bindings';

import type { EyebrowConfig } from '@/app/components/Eyebrow/types';
import { HARDWARE_PDP_ROUTES } from '@/app/consts/pathnames';
import usePathnameWithoutLocale from '@/app/hooks/usePathnameWithoutLocale';

export interface UseEyebrowConfig {
  hasDiscountMessage: boolean;
}

export interface UseEyebrowConfigReturn {
  eyebrowConfig: EyebrowConfig;
  eyebrowMessageType: EyebrowMessageEnum;
}

export enum EyebrowMessageEnum {
  STATSIG_MESSAGE = 'statsig',
  PDP_HARDWARE_MESSAGE = 'pdpHardware',
  AFFILIATE_MESSAGE = 'affiliate',
}

/**
 * Matches a route pattern against a pathname, supporting dynamic segments.
 * Dynamic segments are denoted by square brackets, e.g., [id] or [slug].
 *
 * Examples:
 * - "/store/rings" matches "/store/rings" (exact match)
 * - "/store/oura-ring-4/[finish]" matches "/store/oura-ring-4/black" (dynamic segment)
 */
const matchesRoutePattern = (pattern: string, currentPath: string): boolean => {
  // Exact match
  if (pattern === currentPath) return true;

  const patternParts = pattern.split('/');
  const pathParts = currentPath.split('/');

  // Different number of segments means no match
  if (patternParts.length !== pathParts.length) return false;

  // Check each segment - dynamic segments (e.g., [id]) match any value
  return patternParts.every((part, index) => {
    if (part.startsWith('[') && part.endsWith(']')) return true;
    return part === pathParts[index];
  });
};

const checkCurrentPathInConfig = (
  config: EyebrowConfig,
  currentPath: string,
): boolean => {
  const routes = config.routes ?? [];
  if (!routes.length) return currentPath === '/';

  return routes.some((route) => matchesRoutePattern(route, currentPath));
};

const useEyebrowConfig = ({
  hasDiscountMessage,
}: UseEyebrowConfig): UseEyebrowConfigReturn => {
  const pathname = usePathnameWithoutLocale();

  // Override config
  const overrideConfig = useDynamicConfig('eyebrow_override')
    .value as EyebrowConfig;
  const isOverridePopulated = Object.keys(overrideConfig).length > 0;
  const overrideMatchesRoute =
    isOverridePopulated && checkCurrentPathInConfig(overrideConfig, pathname);

  // Default config
  const defaultConfig = useDynamicConfig('eyebrow').value as EyebrowConfig;
  const isDefaultPopulated = Object.keys(defaultConfig).length > 0;
  const defaultMatchesRoute =
    isDefaultPopulated && checkCurrentPathInConfig(defaultConfig, pathname);

  // Determine which config to use
  const eyebrowConfig = overrideMatchesRoute ? overrideConfig : defaultConfig;

  const isHardwareRoute = HARDWARE_PDP_ROUTES.includes(pathname);
  const hasConfigMatch = overrideMatchesRoute || defaultMatchesRoute;

  let eyebrowMessageType: EyebrowMessageEnum;
  if (hasDiscountMessage && isHardwareRoute) {
    eyebrowMessageType = EyebrowMessageEnum.PDP_HARDWARE_MESSAGE;
  } else if (hasConfigMatch) {
    eyebrowMessageType = EyebrowMessageEnum.STATSIG_MESSAGE;
  } else {
    eyebrowMessageType = EyebrowMessageEnum.AFFILIATE_MESSAGE;
  }

  return { eyebrowConfig, eyebrowMessageType };
};

export default useEyebrowConfig;
